// @ts-nocheck
import { forwardRef, useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Button,
  SegmentedControl,
  Badge,
  Group,
} from '@mantine/core';
import { IconEye, IconSparkles } from '@tabler/icons-react';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import AiInsightCard from './AiInsightCard';
import type { AiInsightFeedProps, AiInsightCategory } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * AiInsightFeed - Feed of AI insights
 *
 * Displays multiple AI insights with filtering and pagination.
 */
const AiInsightFeed = forwardRef<HTMLDivElement, AiInsightFeedProps>(
  (
    {
      insights,
      maxItems = 5,
      filterByCategory,
      filterByConfidence = 0,
      onViewAll,
      onDismiss,
      variant = 'default',
      className,
    },
    ref
  ) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Filter insights
    let filteredInsights = insights;

    // Filter by category
    if (selectedCategory !== 'all') {
      filteredInsights = filteredInsights.filter(
        (insight) => insight.category === selectedCategory
      );
    }

    if (filterByCategory && filterByCategory.length > 0) {
      filteredInsights = filteredInsights.filter((insight) =>
        filterByCategory.includes(insight.category)
      );
    }

    // Filter by confidence
    if (filterByConfidence > 0) {
      filteredInsights = filteredInsights.filter(
        (insight) => (insight.confidence || 0) >= filterByConfidence
      );
    }

    // Sort by confidence (highest first)
    const sortedInsights = [...filteredInsights].sort(
      (a, b) => (b.confidence || 0) - (a.confidence || 0)
    );

    // Limit to maxItems
    const displayInsights = sortedInsights.slice(0, maxItems);
    const hasMore = sortedInsights.length > maxItems;

    // Count by category
    const categoryCounts = insights.reduce((acc, insight) => {
      acc[insight.category] = (acc[insight.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const categories = [
      { value: 'all', label: 'All' },
      { value: 'trend', label: 'Trends' },
      { value: 'anomaly', label: 'Anomalies' },
      { value: 'recommendation', label: 'Recommendations' },
      { value: 'forecast', label: 'Forecasts' },
      { value: 'optimization', label: 'Optimizations' },
      { value: 'risk', label: 'Risks' },
    ];

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            {/* Header */}
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <IconSparkles size={24} color="#8b5cf6" />
                <Text size="lg" fw={600} style={accessibilityStyles}>
                  AI Insights
                </Text>
              </Group>

              <Badge variant="light" color="violet" size="lg">
                {insights.length} insights
              </Badge>
            </Group>

            {/* Category filter */}
            <SegmentedControl
              value={selectedCategory}
              onChange={setSelectedCategory}
              data={categories.map((cat) => ({
                value: cat.value,
                label:
                  cat.value === 'all'
                    ? `All (${insights.length})`
                    : `${cat.label} (${categoryCounts[cat.value] || 0})`,
              }))}
              fullWidth
              size="xs"
            />

            {/* Insights list */}
            {displayInsights.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl" style={accessibilityStyles}>
                No insights to display
              </Text>
            ) : (
              <Stack gap="sm">
                {displayInsights.map((insight) => (
                  <AiInsightCard
                    key={insight.id}
                    insight={insight}
                    variant={variant}
                    showConfidence
                    showTimestamp
                    showCategory
                    onDismiss={onDismiss}
                  />
                ))}
              </Stack>
            )}

            {/* View all button */}
            {(hasMore || onViewAll) && (
              <Button
                variant="gradient"
                gradient={{ from: 'violet', to: 'cyan', deg: 45 }}
                fullWidth
                leftSection={<IconEye size={16} />}
                onClick={onViewAll}
              >
                {hasMore
                  ? `View all ${sortedInsights.length} insights`
                  : 'View all insights'}
              </Button>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

AiInsightFeed.displayName = 'AiInsightFeed';

export default AiInsightFeed;
